import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dumbbell, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
      verifyToken(tokenParam);
    } else {
      toast.error('Token no encontrado');
      setTimeout(() => navigate('/login'), 2000);
    }
  }, [searchParams, navigate]);

  const verifyToken = async (tokenToVerify) => {
    try {
      await axios.get(`/auth/verify-reset-token/${tokenToVerify}`);
      setTokenValid(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Token inválido o expirado');
      setTokenValid(false);
      setTimeout(() => navigate('/login'), 3000);
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      await axios.post('/auth/reset-password', {
        token: token,
        new_password: newPassword
      });

      setSuccess(true);
      toast.success('¡Contraseña actualizada exitosamente!');
      
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar la contraseña');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-content">
          <Card className="reset-card">
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Verificando token...</p>
            </div>
          </Card>
        </div>

        <style jsx>{`
          .reset-password-container {
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
            padding: 2rem;
          }

          .reset-password-content {
            width: 100%;
            max-width: 450px;
          }

          .reset-card {
            padding: 3rem 2rem;
          }

          .loading-state {
            text-align: center;
          }

          .spinner {
            width: 40px;
            height: 40px;
            border: 4px solid #e2e8f0;
            border-top-color: #10b981;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin: 0 auto 1rem;
          }

          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (success) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-content">
          <Card className="reset-card">
            <div className="success-state">
              <CheckCircle size={64} className="success-icon" />
              <h2>¡Contraseña Actualizada!</h2>
              <p>Tu contraseña ha sido actualizada exitosamente.</p>
              <p className="redirect-text">Redirigiendo al inicio de sesión...</p>
            </div>
          </Card>
        </div>

        <style jsx>{`
          .reset-password-container {
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
            padding: 2rem;
          }

          .reset-password-content {
            width: 100%;
            max-width: 450px;
          }

          .reset-card {
            padding: 3rem 2rem;
          }

          .success-state {
            text-align: center;
          }

          .success-icon {
            color: #10b981;
            margin: 0 auto 1.5rem;
          }

          .success-state h2 {
            font-size: 1.875rem;
            font-weight: 700;
            color: #1e293b;
            margin-bottom: 1rem;
          }

          .success-state p {
            color: #64748b;
            margin-bottom: 0.5rem;
          }

          .redirect-text {
            font-size: 0.875rem;
            color: #10b981;
            font-weight: 600;
            margin-top: 1rem;
          }
        `}</style>
      </div>
    );
  }

  if (!tokenValid) {
    return null;
  }

  return (
    <div className="reset-password-container">
      <div className="reset-password-content">
        <div className="branding">
          <div className="brand-icon">
            <Dumbbell size={40} strokeWidth={2.5} />
          </div>
          <h1 className="brand-title">GymHub Admin</h1>
        </div>

        <Card className="reset-card">
          <div className="reset-header">
            <h2>Nueva Contraseña</h2>
            <p>Ingresa tu nueva contraseña</p>
          </div>

          <form onSubmit={handleSubmit} className="reset-form">
            <div className="form-group">
              <Label htmlFor="new-password">Nueva Contraseña*</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Mínimo 6 caracteres"
                required
                data-testid="new-password-input"
              />
            </div>

            <div className="form-group">
              <Label htmlFor="confirm-password">Confirmar Contraseña*</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repite la contraseña"
                required
                data-testid="confirm-password-input"
              />
            </div>

            <Button 
              type="submit" 
              className="submit-btn" 
              disabled={loading}
              data-testid="reset-submit-btn"
            >
              {loading ? 'Actualizando...' : 'Actualizar Contraseña'}
            </Button>
          </form>
        </Card>
      </div>

      <style jsx>{`
        .reset-password-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          padding: 2rem;
        }

        .reset-password-content {
          width: 100%;
          max-width: 450px;
        }

        .branding {
          text-align: center;
          margin-bottom: 2rem;
        }

        .brand-icon {
          width: 80px;
          height: 80px;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1rem;
          border: 1px solid rgba(255, 255, 255, 0.3);
          color: white;
        }

        .brand-title {
          font-size: 2rem;
          font-weight: 800;
          color: white;
          letter-spacing: -0.02em;
        }

        .reset-card {
          padding: 2.5rem;
          border: none;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15);
        }

        .reset-header {
          margin-bottom: 2rem;
          text-align: center;
        }

        .reset-header h2 {
          font-size: 1.875rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 0.5rem;
        }

        .reset-header p {
          color: #64748b;
          font-size: 0.875rem;
        }

        .reset-form {
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .submit-btn {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          padding: 0.75rem;
          font-weight: 600;
          margin-top: 0.5rem;
          height: 48px;
          border-radius: 8px;
        }

        .submit-btn:hover {
          background: linear-gradient(135deg, #059669 0%, #047857 100%);
        }

        @media (max-width: 640px) {
          .reset-password-container {
            padding: 1rem;
          }

          .reset-card {
            padding: 2rem 1.5rem;
          }

          .brand-title {
            font-size: 1.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default ResetPassword;
